import { Placement } from '@floating-ui/react'
import { useIntl } from '@weenat/wintl'
import DiscreteDataMarker from '../dashboard/components/DashboardMap/markers/DiscreteDataMarker'
import MarkerText from '../dashboard/components/DashboardMap/markers/MarkerText'
import PureDataMarker from '../dashboard/components/DashboardMap/markers/PureDataMarker'
import useDisclosure from '../hooks/useDisclosure'
import Button from '../kit/Button'
import { IconProps } from '../kit/Icon'
import Icons from '../kit/Icons'
import Modal from '../kit/Modal'
import Text from '../kit/Text'
import TextTooltip from '../kit/tooltips/TextTooltip'

const WithDataMarkerText = <MarkerText $color={'grayscale.white'}>67 %</MarkerText>

const EmptyMarkerText = <MarkerText $color={'grayscale.black'}>− %</MarkerText>

interface Props {
  tooltipPlacement?: Placement
  variant?: 'button' | 'icon'
  label?: string
  iconProps?: IconProps
}

interface MarkerModalProps {
  isOpen: boolean
  close: () => void
}

interface LegendSectionProps {
  title: string
  description: string
}

const LegendSection: FC<LegendSectionProps> = ({ title, description, children }) => {
  return (
    <Flex $flexDirection='column' $p='xl' $gap='md' $flexBasis='50%'>
      <Flex $gap='xl'>{children}</Flex>
      <Flex $flexDirection='column' $gap='sm'>
        <Text $fontWeight='regular'>{title}</Text>
        <Text>{description}</Text>
      </Flex>
    </Flex>
  )
}

const MarkerModal: FC<MarkerModalProps> = ({ isOpen, close }) => {
  const { t } = useIntl()

  return (
    <Modal
      isOpen={isOpen}
      title={t('map.legend_modal.title')}
      hasCloseIcon={true}
      close={close}
      width={720}
      $noInnerPadding
    >
      <Flex $flexWrap='wrap'>
        <LegendSection
          title={t('map.legend_modal.markers_section.no_data.title')}
          description={t('map.legend_modal.markers_section.no_data.description')}
        >
          <DiscreteDataMarker hideAnchor type='plot' />
          <DiscreteDataMarker hideAnchor type='device' />
          <DiscreteDataMarker hideAnchor type='virtual_device' />
        </LegendSection>
        <LegendSection
          title={t('map.legend_modal.markers_section.with_data.title')}
          description={t('map.legend_modal.markers_section.with_data.description')}
        >
          {
            <>
              <PureDataMarker
                hideAnchor
                type='plot'
                backgroundColor={'metrics.U.500'}
                color={'grayscale.white'}
                showContent
                content={WithDataMarkerText}
                isFocused={false}
                lastUpdate={undefined}
                showLastUpdate={false}
                trend={undefined}
                showStatusIndicator={false}
                status={undefined}
                timezone='Europe/Paris'
              />
              <PureDataMarker
                hideAnchor
                type='device'
                backgroundColor={'metrics.U.500'}
                color={'grayscale.white'}
                showContent
                content={WithDataMarkerText}
                isFocused={false}
                lastUpdate={undefined}
                showLastUpdate={false}
                trend={undefined}
                showStatusIndicator={false}
                status={undefined}
                timezone='Europe/Paris'
              />
              <PureDataMarker
                hideAnchor
                type='virtual_device'
                backgroundColor={'metrics.U.500'}
                color={'grayscale.white'}
                showContent
                content={WithDataMarkerText}
                isFocused={false}
                lastUpdate={undefined}
                showLastUpdate={false}
                trend={undefined}
                showStatusIndicator={false}
                status={undefined}
                timezone='Europe/Paris'
              />
            </>
          }
        </LegendSection>
        <LegendSection
          title={t('map.legend_modal.markers_section.empty.title')}
          description={t('map.legend_modal.markers_section.empty.description')}
        >
          {
            <>
              <PureDataMarker
                hideAnchor
                type='plot'
                backgroundColor={'grayscale.100'}
                color={'grayscale.black'}
                showContent
                content={EmptyMarkerText}
                isFocused={false}
                lastUpdate={undefined}
                showLastUpdate={false}
                trend={undefined}
                showStatusIndicator={false}
                status={undefined}
                timezone='Europe/Paris'
              />
              <PureDataMarker
                hideAnchor
                type='device'
                backgroundColor={'grayscale.100'}
                color={'grayscale.black'}
                showContent
                content={EmptyMarkerText}
                isFocused={false}
                lastUpdate={undefined}
                showLastUpdate={false}
                trend={undefined}
                showStatusIndicator={false}
                status={undefined}
                timezone='Europe/Paris'
              />
              <PureDataMarker
                hideAnchor
                type='virtual_device'
                backgroundColor={'grayscale.100'}
                color={'grayscale.black'}
                showContent
                content={EmptyMarkerText}
                isFocused={false}
                lastUpdate={undefined}
                showLastUpdate={false}
                trend={undefined}
                showStatusIndicator={false}
                status={undefined}
                timezone='Europe/Paris'
              />
            </>
          }
        </LegendSection>
        <LegendSection
          title={t('map.legend_modal.markers_section.needs_config.title')}
          description={t('map.legend_modal.markers_section.needs_config.description')}
        >
          <PureDataMarker
            hideAnchor
            type='plot'
            backgroundColor={'grayscale.100'}
            color={'grayscale.black'}
            showContent={false}
            content={undefined}
            isFocused={false}
            lastUpdate={undefined}
            showLastUpdate={false}
            trend={undefined}
            showStatusIndicator
            status={'config'}
            timezone='Europe/Paris'
          />
        </LegendSection>
        <LegendSection
          title={t('map.legend_modal.markers_section.no_emission.title')}
          description={t('map.legend_modal.markers_section.no_emission.description')}
        >
          <>
            <PureDataMarker
              hideAnchor
              type='plot'
              backgroundColor={'grayscale.100'}
              color={'grayscale.black'}
              showContent={false}
              content={undefined}
              isFocused={false}
              lastUpdate={undefined}
              showLastUpdate={false}
              trend={undefined}
              showStatusIndicator
              status={'not_emitted'}
              timezone='Europe/Paris'
            />
            <PureDataMarker
              hideAnchor
              type='device'
              backgroundColor={'grayscale.100'}
              color={'grayscale.black'}
              showContent={false}
              content={undefined}
              isFocused={false}
              lastUpdate={undefined}
              showLastUpdate={false}
              trend={undefined}
              showStatusIndicator
              status={'not_emitted'}
              timezone='Europe/Paris'
            />
            <PureDataMarker
              hideAnchor
              type='virtual_device'
              backgroundColor={'grayscale.100'}
              color={'grayscale.black'}
              showContent={false}
              content={undefined}
              isFocused={false}
              lastUpdate={undefined}
              showLastUpdate={false}
              trend={undefined}
              showStatusIndicator
              status={'not_emitted'}
              timezone='Europe/Paris'
            />
          </>
        </LegendSection>
      </Flex>
    </Modal>
  )
}

const MarkerLegendModal = ({ tooltipPlacement, variant = 'icon', label, iconProps }: Props) => {
  const { t } = useIntl()
  const { isOpen, open, close } = useDisclosure()

  const finalLabel = t('map.show_legend') ?? label
  return (
    <>
      <MarkerModal isOpen={isOpen} close={close} />
      {variant === 'icon' && (
        <TextTooltip content={finalLabel} placement={tooltipPlacement}>
          <Icons.NotListedLocation onPress={open} $size={'xl'} $m='sm' $p='xs' {...iconProps} />
        </TextTooltip>
      )}
      {variant === 'button' && (
        <Button importance='sd' IconRight={Icons.NotListedLocation}>
          {finalLabel}
        </Button>
      )}
    </>
  )
}

export default MarkerLegendModal
